<template>
  <div>
    <!-- BREADCRUMB -->
    <b-row class="pt-2 pb-1">
      <b-col cols="6">
        <BreadcrumbMenu />
      </b-col>
    </b-row>

    <!-- FILTERS MODAL -->
    <b-modal
      id="user-group-selection-filters-modal"
      v-model="isFiltersModalOpen"
      :title="$t('user_group_selection_option.bulk.filters')"
      centered
      no-close-on-backdrop
      header-bg-variant="dark"
      header-text-variant="light"
      size="lg"
    >
      <filters :user-filter.sync="userFilter" />

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          @click="clearFilters"
        >
          {{ $t('user_group_selection_option.bulk.clear_filters') }}
        </b-button>

        <b-button
          variant="primary"
          @click="applyFilters"
        >
          {{ $t('user_group_selection_option.bulk.apply_filters') }}
        </b-button>
      </template>
    </b-modal>

    <!-- BULK MODAL -->
    <group-users-bulk-edit-modal
      :is-open="isBulkModalOpen"
      :group-ids="bulkGroupIds"
      :selected-count="bulkGroupIds.length"
      :is-submitting="isBulkSubmitting"
      @close="closeBulkModal"
      @submit="submitBulkUpdate"
    />

    <!-- TABLE CARD -->
    <b-card
      class="m-2"
      no-body
    >
      <div class="m-2">
        <b-row class="align-items-center">
          <b-col
            cols="12"
            md="4"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="text-capitalize">
              {{ $t("table.show") }}
            </label>

            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />

            <label class="text-capitalize">
              {{ $t("table.entries") }}
            </label>
          </b-col>

          <b-col
            cols="12"
            md="8"
          >
            <div class="d-flex align-items-center justify-content-end flex-wrap">
              <div
                v-if="selectedCount"
                class="d-flex align-items-center mr-1 mb-50 mb-md-0"
              >
                <b-badge
                  variant="light-primary"
                  class="mr-50"
                >
                  {{ selectedCount }}
                  {{ $tc('user_group_selection_option.bulk.selected_groups', selectedCount) }}
                </b-badge>

                <b-button
                  variant="flat-secondary"
                  size="sm"
                  @click="clearSelection"
                >
                  {{ $t('user_group_selection_option.bulk.clear') }}
                </b-button>
              </div>

              <b-form-input
                v-model="searchQuery"
                class="search-input d-inline-block mr-1"
                :placeholder="$t('user_group_selection_option.bulk.search_group')"
              />

              <b-button
                variant="outline-secondary"
                class="mr-1 position-relative"
                @click="isFiltersModalOpen = true"
              >
                <feather-icon
                  icon="FilterIcon"
                  size="16"
                  class="mr-50"
                />

                {{ $t('user_group_selection_option.bulk.filters') }}
                <b-badge
                  v-if="activeFiltersCount"
                  pill
                  variant="primary"
                  class="filter-count-badge"
                >
                  {{ activeFiltersCount }}
                </b-badge>
              </b-button>

              <b-button
                v-if="
                  $can('create', 'UserGroupSelectionOption') ||
                  $can('update', 'UserGroupSelectionOption')
                "
                variant="secondary"
                :disabled="!selectedCount"
                @click="openBulkForSelection"
              >
                <feather-icon
                  icon="UsersIcon"
                  size="16"
                  class="mr-50"
                />

                <span class="text-nowrap">
                  {{ $t('user_group_selection_option.bulk.manage_users') }}
                </span>
              </b-button>
            </div>
          </b-col>
        </b-row>

        <div
          v-if="userFilter"
          class="d-flex align-items-center mt-1"
        >
          <span class="text-muted small mr-50">
            {{ $t('user_group_selection_option.bulk.filtering_user') }}
          </span>

          <b-badge
            variant="light-primary"
            class="mr-50"
          >
            {{ getUserFilterLabel() }}
          </b-badge>

          <b-button
            variant="flat-secondary"
            size="sm"
            @click="clearFilters"
          >
            <feather-icon
              icon="XIcon"
              size="13"
            />
          </b-button>
        </div>
      </div>

      <!-- TABLE -->
      <b-table
        ref="refListTable"
        class="position-relative"
        :items="fetchList"
        :fields="tableColumns"
        responsive
        primary-key="id"
        :sort-by.sync="sortBy"
        :sort-desc.sync="isSortDirDesc"
        show-empty
        :empty-text="$t('no_record_found')"
      >
        <!-- SELECT ALL -->
        <template #head(selected)>
          <b-form-checkbox
            :checked="allCurrentPageSelected"
            :indeterminate="someCurrentPageSelected"
            @change="toggleCurrentPage"
          />
        </template>

        <!-- SELECT ROW -->
        <template #cell(selected)="{ item }">
          <b-form-checkbox
            :checked="isGroupSelected(item.id)"
            @change="(checked) => toggleGroup(item.id, checked)"
          />
        </template>

        <!-- ACTIONS -->
        <template #head(actions)="data">
          <span>
            {{ $tc(data.label, 2) }}
          </span>
        </template>

        <template #cell(actions)="{ item }">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item
              v-if="
                $can('create', 'UserGroupSelectionOption') ||
                $can('update', 'UserGroupSelectionOption')
              "
              @click="openBulkForSingle(item)"
            >
              <feather-icon icon="UsersIcon" />

              <span class="align-middle ml-50">
                {{ $t('user_group_selection_option.bulk.manage_users') }}
              </span>
            </b-dropdown-item>
          </b-dropdown>
        </template>

        <!-- GROUP -->
        <template #head(name)="data">
          <span>
            {{ $t(data.label) }}
          </span>
        </template>

        <template #cell(name)="{ item }">
          <div class="d-flex align-items-center">
            <div class="group-icon mr-1">
              <feather-icon
                icon="UsersIcon"
                size="16"
              />
            </div>

            <div>
              <div class="font-weight-bold">
                {{ item.name }}
              </div>

              <small class="text-muted">
                ID: {{ item.id }}
              </small>
            </div>
          </div>
        </template>

        <!-- USERS COUNT -->
        <template #head(users_count)="data">
          <span>
            {{ $t(data.label) }}
          </span>
        </template>

        <template #cell(users_count)="{ item }">
          <div class="d-inline-block">
            <b-badge
              :id="`group-users-preview-${item.id}`"
              :variant="
                Number(item.users_count) > 0
                  ? 'light-primary'
                  : 'light-secondary'
              "
              class="users-count-badge"
              tabindex="0"
              role="button"
              @click="toggleGroupUsersPreview(item)"
              @keydown.enter.prevent="toggleGroupUsersPreview(item)"
              @keydown.space.prevent="toggleGroupUsersPreview(item)"
            >
              {{ Number(item.users_count) || 0 }}

              {{ $tc('user_group_selection_option.bulk.user_count', Number(item.users_count)) }}

              <feather-icon
                v-if="Number(item.users_count) > 0"
                icon="UsersIcon"
                size="12"
                class="ml-25"
              />
            </b-badge>

            <b-popover
              v-if="Number(item.users_count) > 0"
              :target="`group-users-preview-${item.id}`"
              :show.sync="groupUsersPreviewVisible[item.id]"
              triggers="manual"
              placement="left"
              custom-class="group-users-popover"
            >
              <template #title>
                <div class="d-flex align-items-center justify-content-between">
                  <span>
                    {{ $t('user_group_selection_option.bulk.authorized_users') }}
                  </span>

                  <b-button
                    variant="flat-secondary"
                    size="sm"
                    class="popover-close-button"
                    @click="closeGroupUsersPreview(item.id)"
                  >
                    <feather-icon
                      icon="XIcon"
                      size="14"
                    />
                  </b-button>
                </div>
              </template>

              <div
                v-if="
                  isGroupPreviewLoading(item.id) &&
                  !getGroupPreview(item.id)
                "
                class="text-center py-2"
              >
                <b-spinner small />

                <div class="text-muted small mt-50">
                  {{ $t('user_group_selection_option.bulk.loading_users') }}
                </div>
              </div>

              <template
                v-else-if="getGroupPreview(item.id)"
              >
                <div
                  v-if="
                    getGroupPreview(item.id).users &&
                    getGroupPreview(item.id).users.length
                  "
                  class="group-users-preview-list"
                  @scroll="handleUsersPreviewScroll($event, item)"
                >
                  <div
                    v-for="user in getGroupPreview(item.id).users"
                    :key="user.id"
                    class="group-user-preview-item"
                  >
                    <div class="font-weight-bold small">
                      {{ user.full_name }}
                    </div>

                    <div class="text-muted preview-email">
                      {{
                        user.username ||
                        user.user_name ||
                        user.login ||
                        user.email
                      }}
                    </div>
                  </div>

                  <div
                    v-if="isGroupPreviewLoading(item.id)"
                    class="text-center py-1"
                  >
                    <b-spinner small />

                    <div class="text-muted small mt-25">
                      {{ $t('user_group_selection_option.bulk.loading_more') }}
                    </div>
                  </div>

                  <div
                    v-else-if="!getGroupPreview(item.id).has_more"
                    class="text-center text-muted small py-1"
                  >
                    {{ $t('user_group_selection_option.bulk.users_shown', { count: getGroupPreview(item.id).users.length, total: getGroupPreview(item.id).total }) }}
                  </div>
                </div>

                <div
                  v-else
                  class="text-muted small py-1"
                >
                  {{ $t('user_group_selection_option.bulk.no_authorized_users') }}
                </div>
              </template>
            </b-popover>
          </div>
        </template>
      </b-table>

      <!-- PAGINATION -->
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted text-capitalize">
              {{
                $t("table.showing", {
                  from: dataMeta.from,
                  to: dataMeta.to,
                  of: dataMeta.of,
                })
              }}
            </span>
          </b-col>

          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <Pagination
              :totalList="totalList"
              :currentPage="currentPage"
              :perPage="perPage"
              @updatePagination="update"
            />
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import i18n from "@/libs/i18n";
import {
  BBadge,
  BButton,
  BCard,
  BCol,
  BDropdown,
  BDropdownItem,
  BFormCheckbox,
  BFormInput,
  BModal,
  BPopover,
  BRow,
  BSpinner,
  BTable,
} from "bootstrap-vue";
import {
  computed,
  ref,
} from "@vue/composition-api";
import vSelect from "vue-select";
import {
  useToast,
} from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import Pagination from "@/components/Pagination.vue";
import store from "@/store";
import {
  getResponseErrors,
} from "@/utils/responseErrors";
import useList from "./useList";
import Filters from "./Filters.vue";
import GroupUsersBulkEditModal from "./GroupUsersBulkEditModal.vue";

export default {
  name: "UserGroupSelectionOptionIndex",

  components: {
    BBadge,
    BButton,
    BCard,
    BCol,
    BDropdown,
    BDropdownItem,
    BFormCheckbox,
    BFormInput,
    BModal,
    BPopover,
    BRow,
    BSpinner,
    BTable,
    vSelect,
    BreadcrumbMenu,
    Pagination,
    Filters,
    GroupUsersBulkEditModal,
  },

  setup() {
    const toast = useToast();

    const {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      userFilter,
      selectedGroupIds,
      selectedCount,
      allCurrentPageSelected,
      someCurrentPageSelected,
      isGroupSelected,
      toggleGroup,
      toggleCurrentPage,
      clearSelection,
    } = useList();

    const isFiltersModalOpen =
      ref(false);

    const activeFiltersCount =
      computed(() => {
        return userFilter.value
          ? 1
          : 0;
      });

    const getUserFilterLabel = () => {
      const user = userFilter.value;

      if (!user) {
        return "";
      }

      if (
        typeof user !== "object"
      ) {
        return i18n.t("user_group_selection_option.bulk.user_fallback", { id: user });
      }

      return (
        user.full_name ??
        user.label ??
        user.email ??
        i18n.t("user_group_selection_option.bulk.user_fallback", { id: user.id ?? user.value ?? "" })
      );
    };

    const bulkGroupIds = ref([]);
    const isBulkModalOpen = ref(false);
    const isBulkSubmitting = ref(false);

    const groupUsersPreview = ref({});
    const groupUsersPreviewLoading = ref({});
    const groupUsersPreviewVisible = ref({});
    const USERS_PREVIEW_LIMIT = 10;

    const update = (page) => {
      currentPage.value = page;
    };

    const clearPreviewCache = () => {
      groupUsersPreview.value = {};
      groupUsersPreviewLoading.value = {};
      groupUsersPreviewVisible.value = {};
    };

    const applyFilters = () => {
      currentPage.value = 1;
      clearSelection();
      clearPreviewCache();
      isFiltersModalOpen.value = false;
      refetchData();
    };

    const clearFilters = () => {
      userFilter.value = null;
      currentPage.value = 1;
      clearSelection();
      clearPreviewCache();
      isFiltersModalOpen.value = false;
      refetchData();
    };

    const openBulkForSelection = () => {
      if (
        !selectedGroupIds.value.length
      ) {
        return;
      }

      bulkGroupIds.value = [
        ...selectedGroupIds.value,
      ];

      isBulkModalOpen.value = true;
    };

    const openBulkForSingle = (item) => {
      bulkGroupIds.value = [
        item.id,
      ];

      isBulkModalOpen.value = true;
    };

    const closeBulkModal = () => {
      if (isBulkSubmitting.value) {
        return;
      }

      isBulkModalOpen.value = false;
      bulkGroupIds.value = [];
    };

    const toggleGroupUsersPreview =
      async (item) => {
        const groupId = item.id;

        if (
          groupUsersPreviewVisible.value[
            groupId
          ]
        ) {
          closeGroupUsersPreview(
            groupId
          );

          return;
        }

        groupUsersPreviewVisible.value = {
          [groupId]: true,
        };

        if (
          !groupUsersPreview.value[
            groupId
          ]
        ) {
          await loadGroupUsersPreview(
            item
          );
        }
      };

    const closeGroupUsersPreview =
      (groupId) => {
        groupUsersPreviewVisible.value = {
          ...groupUsersPreviewVisible.value,
          [groupId]: false,
        };
      };

    const loadGroupUsersPreview =
      async (
        item,
        append = false
      ) => {
        const groupId = item.id;

        if (
          groupUsersPreviewLoading.value[
            groupId
          ]
        ) {
          return;
        }

        const currentPreview =
          groupUsersPreview.value[
            groupId
          ];

        if (
          append &&
          currentPreview &&
          !currentPreview.has_more
        ) {
          return;
        }

        const currentUsers =
          append &&
          currentPreview &&
          Array.isArray(
            currentPreview.users
          )
            ? currentPreview.users
            : [];

        const offset =
          append
            ? currentUsers.length
            : 0;

        groupUsersPreviewLoading.value = {
          ...groupUsersPreviewLoading.value,
          [groupId]: true,
        };

        try {
          const response =
            await store.dispatch(
              "user_group_selection_option/getGroupUsers",
              {
                group_ids: [
                  groupId,
                ],
                limit:
                  USERS_PREVIEW_LIMIT,
                offset,
              }
            );

          const groups =
            response?.data?.groups ??
            response?.groups ??
            [];

          const group =
            groups.length
              ? groups[0]
              : null;

          if (!group) {
            return;
          }

          if (!append) {
            groupUsersPreview.value = {
              ...groupUsersPreview.value,

              [groupId]: {
                ...group,

                users:
                  Array.isArray(
                    group.users
                  )
                    ? group.users
                    : [],
              },
            };

            return;
          }

          const newUsers =
            Array.isArray(
              group.users
            )
              ? group.users
              : [];

          const mergedUsers = [
            ...currentUsers,
            ...newUsers,
          ].filter(
            (
              user,
              index,
              array
            ) =>
              array.findIndex(
                (currentUser) =>
                  Number(
                    currentUser.id
                  ) ===
                  Number(
                    user.id
                  )
              ) === index
          );

          const total =
            Number(
              group.total
            ) || 0;

          groupUsersPreview.value = {
            ...groupUsersPreview.value,

            [groupId]: {
              ...group,

              users:
                mergedUsers,

              loaded:
                mergedUsers.length,

              has_more:
                mergedUsers.length <
                total,
            },
          };
        } catch (error) {
          console.error(
            "Error loading group users preview:",
            error
          );
        } finally {
          groupUsersPreviewLoading.value = {
            ...groupUsersPreviewLoading.value,
            [groupId]: false,
          };
        }
      };

    const handleUsersPreviewScroll =
      (
        event,
        item
      ) => {
        const element =
          event.target;

        if (!element) {
          return;
        }

        const threshold = 50;

        const distanceToBottom =
          element.scrollHeight -
          element.scrollTop -
          element.clientHeight;

        if (
          distanceToBottom >
          threshold
        ) {
          return;
        }

        const preview =
          groupUsersPreview.value[
            item.id
          ];

        if (
          !preview ||
          !preview.has_more
        ) {
          return;
        }

        if (
          groupUsersPreviewLoading.value[
            item.id
          ]
        ) {
          return;
        }

        loadGroupUsersPreview(
          item,
          true
        );
      };

    const getGroupPreview =
      (groupId) => {
        return (
          groupUsersPreview.value[
            groupId
          ] || null
        );
      };

    const isGroupPreviewLoading =
      (groupId) => {
        return Boolean(
          groupUsersPreviewLoading.value[
            groupId
          ]
        );
      };

    /**
     * El modal ahora manda:
     *
     * add_user_ids:
     * usuarios a agregar a TODOS los grupos seleccionados.
     *
     * removals:
     * usuario + grupo específico.
     */
    const submitBulkUpdate = async ({
      add_user_ids,
      removals,
    }) => {
      const addUserIds =
        Array.isArray(
          add_user_ids
        )
          ? add_user_ids
          : [];

      const removalItems =
        Array.isArray(
          removals
        )
          ? removals
          : [];

      if (
        !bulkGroupIds.value.length ||
        (
          !addUserIds.length &&
          !removalItems.length
        )
      ) {
        return;
      }

      isBulkSubmitting.value = true;

      try {
        /**
         * 1. Agregar usuarios a todos los grupos
         * seleccionados.
         */
        if (
          addUserIds.length
        ) {
          await store.dispatch(
            "user_group_selection_option/bulkUpdate",
            {
              group_ids:
                bulkGroupIds.value,

              user_ids:
                addUserIds,

              action:
                "attach",
            }
          );
        }

        /**
         * 2. Agrupar removals por group_id.
         *
         * En vez de disparar una llamada por usuario,
         * hacemos una llamada por grupo.
         */
        const removalsByGroup =
          removalItems.reduce(
            (
              groups,
              item
            ) => {
              const groupId =
                Number(
                  item.group_id
                );

              const userId =
                Number(
                  item.user_id
                );

              if (
                Number.isNaN(
                  groupId
                ) ||
                Number.isNaN(
                  userId
                )
              ) {
                return groups;
              }

              if (
                !groups[
                  groupId
                ]
              ) {
                groups[
                  groupId
                ] = [];
              }

              if (
                !groups[
                  groupId
                ].includes(
                  userId
                )
              ) {
                groups[
                  groupId
                ].push(
                  userId
                );
              }

              return groups;
            },
            {}
          );

        const removeRequests =
          Object.entries(
            removalsByGroup
          ).map(
            ([
              groupId,
              userIds,
            ]) => {
              return store.dispatch(
                "user_group_selection_option/bulkUpdate",
                {
                  group_ids: [
                    Number(
                      groupId
                    ),
                  ],

                  user_ids:
                    userIds,

                  action:
                    "detach",
                }
              );
            }
          );

        if (
          removeRequests.length
        ) {
          await Promise.all(
            removeRequests
          );
        }

        toast({
          component:
            ToastificationContent,

          props: {
            title:
              i18n.t("user_group_selection_option.bulk.save_success"),

            icon:
              "CheckIcon",

            variant:
              "success",
          },
        });

        isBulkModalOpen.value =
          false;

        bulkGroupIds.value = [];

        clearSelection();

        clearPreviewCache();

        refetchData();
      } catch (error) {
        const {
          message,
          strValidationErrors,
        } = getResponseErrors(
          error
        );

        toast({
          component:
            ToastificationContent,

          props: {
            title:
              strValidationErrors ||
              message ||
              i18n.t("user_group_selection_option.bulk.save_error"),

            icon:
              "AlertTriangleIcon",

            variant:
              "danger",
          },
        });
      } finally {
        isBulkSubmitting.value =
          false;
      }
    };

    return {
      fetchList,
      tableColumns,

      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,

      searchQuery,

      sortBy,
      isSortDirDesc,

      refListTable,

      userFilter,
      isFiltersModalOpen,
      activeFiltersCount,
      getUserFilterLabel,
      applyFilters,
      clearFilters,

      selectedGroupIds,
      selectedCount,

      allCurrentPageSelected,
      someCurrentPageSelected,

      isGroupSelected,
      toggleGroup,
      toggleCurrentPage,
      clearSelection,

      bulkGroupIds,
      isBulkModalOpen,
      isBulkSubmitting,

      openBulkForSelection,
      openBulkForSingle,
      closeBulkModal,
      submitBulkUpdate,

      groupUsersPreview,
      groupUsersPreviewVisible,

      toggleGroupUsersPreview,
      closeGroupUsersPreview,

      loadGroupUsersPreview,
      handleUsersPreviewScroll,

      getGroupPreview,
      isGroupPreviewLoading,

      update,
    };
  },
};
</script>

<style lang="scss" scoped>
.search-input {
  max-width: 300px;
}

.group-icon {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(115, 103, 240, 0.08);
}

.filter-count-badge {
  margin-left: 0.4rem;
}

.users-count-badge {
  cursor: pointer;
  user-select: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  &:focus {
    outline: none;
    box-shadow:
      0 0 0 0.2rem
      rgba(
        115,
        103,
        240,
        0.15
      );
  }
}

.group-users-preview-list {
  min-width: 280px;
  max-width: 320px;
  height: 320px;
  overflow-y: auto;
  padding-right: 0.35rem;
  overscroll-behavior: contain;
}

.group-user-preview-item {
  padding: 0.6rem 0;
  border-bottom:
    1px solid #ebe9f1;

  &:first-child {
    padding-top: 0;
  }
}

.preview-email {
  max-width: 270px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.75rem;
}

.popover-close-button {
  padding: 0;
  line-height: 1;
}

@media (
  max-width: 767px
) {
  .search-input {
    max-width: none;
    width: 100%;
    margin-bottom: 0.5rem;
  }

  .group-users-preview-list {
    min-width: 230px;
    max-width: 260px;
    height: 280px;
  }
}
</style>
